import React, { useEffect, useState, useRef } from "react";
import companyData from "./tableData.json";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchReservationsReservations,
  initializeReservationsReservationsScreen,
  setDeleteConfirmationPopupVisibility,
  exportReservationsReservations,
  setReservationFilters,
  clearReservationFilters,
  updateNoShow,
  setTableColumnFilters,
  clearTableColumnFilters,
  saveReservationTableState,
} from "../../../redux/actions/Reservations/ReservationsReservations";
import { fetchLocationDD } from "../../../redux/actions/location";
import { fetchRateAdminNetworkDD } from "../../../redux/actions/RateAdmin/rateadminRates";
import ReservationsReservations from "../../../component/Reservations/ReservationsReservations";
import { generatePath } from "react-router-dom";
import { loadingShow } from "../../../services/PostAPI";
import { APP_URLS } from "../../../utils/constant";
import { ProgressBar } from "primereact/progressbar";
import { FILTER } from "../../../services/common/urlService";
import { DATE_STRATEGY, formatDate } from "../../../utils/date";

function ReservationsReservationsScreen(props) {
  const {
    fetchReservationsReservations,
    initReservationsReservationsScreen,
    showPageLevelLoader,
    ReservationsReservationsListview,
    paginationInfo,
    screenPermissions,
    exportReservationsReservations,
    isReservationsReservationsExportError,
    isReservationsReservationsExportSuccess,
    isLoading,
    fetchLocationDD,
    fetchRateAdminNetworkDD,
    RateNetworkDD,
    locationDD,
    reservationFilters,
    setReservationFilters,
    clearReservationFilters,
    updateNoShow,
    reservationSummary,
    tableColumnFilters,
    setTableColumnFilters,
    clearTableColumnFilters,
    savedTableState,
    saveReservationTableState,
  } = props;
  const navigate = useNavigate();
  const location = useLocation();

  // Use ref to capture initial savedTableState to avoid closure issues
  const initialSavedState = useRef(savedTableState);

  const buildFetchParamsFromSavedState = (state) => {
    if (!state) return null;
    
    // Helper to check if a string looks like an ISO date
    const isISODateString = (str) => {
      if (typeof str !== 'string') return false;
      return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str);
    };
    
    // Helper to format date values to YYYY-MM-DD
    const formatFilterValue = (value) => {
      if (value instanceof Date) {
        return formatDate(value, DATE_STRATEGY.DATE_ISO);
      }
      if (isISODateString(value)) {
        return formatDate(new Date(value), DATE_STRATEGY.DATE_ISO);
      }
      return value;
    };
    
    // Build filter params in the format expected by the API
    const filters = [];
    if (state.filters) {
      Object.keys(state.filters).forEach(key => {
        if (key !== 'global' && state.filters[key]?.value !== null && state.filters[key]?.value !== undefined) {
          const filterValue = state.filters[key].value;
          filters.push({
            filterField: state.filters[key].filterKey || key,
            matchStrategy: state.filters[key].matchStrategy || FILTER.MATCH_STRATEGY.CONTAINS,
            query: formatFilterValue(filterValue)
          });
        }
      });
    }
    
    return {
      sortField: state.sortField || 'id',
      sortOrder: state.sortOrder,
      pageNumber: state.page || 0,
      pageSize: state.rows || 10,
      filters: filters.length > 0 ? filters : undefined,
      userId: localStorage.getItem("userId"),
      additinalFilter: reservationFilters,
    };
  };

  const callAPIasync = async () => {
    await fetchRateAdminNetworkDD();
    await fetchLocationDD();
  };

  useEffect(() => {
    callAPIasync();
    
    // Check if coming from detail page (back button) BEFORE calling init
    const isFromDetail = location.state?.fromDetail;
    
    if (isFromDetail) {
      // Coming back from detail page - DO NOT call init, preserve filters
      // Fetch with saved table state params if available, otherwise use defaults
      const savedParams = buildFetchParamsFromSavedState(initialSavedState.current);
      if (savedParams) {
        fetchReservationsReservations(savedParams);
      } else {
        // Build column filters array from tableColumnFilters for API call
        const columnFilters = Object.entries(tableColumnFilters || {}).map(([key, value]) => ({
          filterField: key,
          matchStrategy: 'CONTAINS',
          query: value
        })).filter(f => f.query);
        
        // Use saved pagination from initialSavedState if available
        const savedState = initialSavedState.current;
        
        // Fetch with preserved filters and pagination
        fetchReservationsReservations({
          pageNumber: savedState?.page || 0,
          pageSize: savedState?.rows || 10,
          sortField: savedState?.sortField || "id",
          sortOrder: savedState?.sortOrder,
          userId: localStorage.getItem("userId"),
          additinalFilter: reservationFilters,
          filters: columnFilters,
        });
      }
      // Clear the navigation state
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      // Coming from any other menu - call init and clear all filters
      initReservationsReservationsScreen();
      clearReservationFilters();
      clearTableColumnFilters();
      // Don't fetch on initial load - user will apply filters and search
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableStateChange = (tableState) => {
    saveReservationTableState(tableState);
  };

  const loadLazyData = (props) => {
    let newProps = {
      ...props,
      userId: localStorage.getItem("userId"),
      additinalFilter: props.additinalFilter ?? reservationFilters,
    };
    fetchReservationsReservations(newProps);
  };

  const handleCreateReservationsReservations = () => {
    navigate(APP_URLS.RESERVATIONS_RESERVATIONS.CREATE);
  };
  const editReservationsReservationsRoute = (id) => {
    const path = generatePath(APP_URLS.RESERVATIONS_RESERVATIONS.VIEW, {
      id,
    });
    navigate(path, { state: { fromList: true } });
  };
  const handleUpdateNoShow = (reservationID) => {
    updateNoShow({ reservationID }, () => {
      // Refresh the list after successful update
      fetchReservationsReservations({
        sortField: "id",
        userId: localStorage.getItem("userId"),
        additinalFilter: reservationFilters,
      });
    });
  };

  const ReservationsReservationsProps = {
    handleCreateReservationsReservations,
    exportReservationsReservations,
    isReservationsReservationsExportError,
    isReservationsReservationsExportSuccess,
    editReservationsReservationsRoute,
    screenPermissions,
    setDeleteConfirmationPopupVisibility,
    fetchReservationsReservations: loadLazyData,
    tableData: {
      columns: ReservationsReservationsListview?.columns,
      rows: ReservationsReservationsListview?.rows,
    },
    paginationInfo: {
      pageSize: paginationInfo?.pageSize,
      pageNumber: paginationInfo?.pageNumber,
      totalPages: paginationInfo?.totalPages,
      totalRecords: paginationInfo?.totalRecords,
    },
    isLoading,
    RateNetworkDD,
    locationDD,
    reservationFilters,
    setReservationFilters,
    clearReservationFilters,
    handleNoShowClick: handleUpdateNoShow,
    reservationSummary,
    tableColumnFilters,
    setTableColumnFilters,
    clearTableColumnFilters,
    savedTableState,
    onTableStateChange: handleTableStateChange,
  };

  const renderProgressBar = () => {
    return <ProgressBar mode="indeterminate" style={{ height: "6px" }} />;
  };

  if (showPageLevelLoader) {
    loadingShow("block");
    return null;
  }
  loadingShow("none");
  return (
    <div>
      {isLoading && renderProgressBar()}
      <ReservationsReservations {...ReservationsReservationsProps} />
    </div>
  );
}

const mapDispatchToProps = (dispatch) => {
  return {
    initReservationsReservationsScreen: () =>
      dispatch(initializeReservationsReservationsScreen()),
    fetchReservationsReservations: (payload) =>
      dispatch(fetchReservationsReservations(payload)),
    setDeleteConfirmationPopupVisibility: (payload) =>
      dispatch(setDeleteConfirmationPopupVisibility(payload)),
    fetchRateAdminNetworkDD: (payload) =>
      dispatch(fetchRateAdminNetworkDD(payload)),
    fetchLocationDD: (payload) => dispatch(fetchLocationDD(payload)),
    exportReservationsReservations: (payload) =>
      dispatch(exportReservationsReservations(payload)),
    setReservationFilters: (filters) =>
      dispatch(setReservationFilters(filters)),
    clearReservationFilters: () => dispatch(clearReservationFilters()),
    updateNoShow: (payload, onSuccess) =>
      dispatch(updateNoShow(payload, onSuccess)),
    setTableColumnFilters: (filters) =>
      dispatch(setTableColumnFilters(filters)),
    clearTableColumnFilters: () => dispatch(clearTableColumnFilters()),
    saveReservationTableState: (payload) =>
      dispatch(saveReservationTableState(payload)),
  };
};

//vehicleCagegoryReducer
const mapStateToProps = (state, ownProps) => {
  return {
    isReservationsReservationsCreateSuccess:
      selectFromReservationsReservationsReducer(
        state,
        "isReservationsReservationsCreateSuccess",
      ),
    isLoading: selectFromReservationsReservationsReducer(state, "isLoading"),
    ReservationsReservationsListview: selectFromReservationsReservationsReducer(
      state,
      "ReservationsReservationsListview",
    ),
    showPageLevelLoader: selectFromReservationsReservationsReducer(
      state,
      "showPageLevelLoader",
    ),
    paginationInfo: selectFromReservationsReservationsReducer(
      state,
      "paginationInfo",
    ),
    isPageLevelError: selectFromReservationsReservationsReducer(
      state,
      "isPageLevelError",
    ),
    isFetchingGridData: selectFromReservationsReservationsReducer(
      state,
      "isFetchingGridData",
    ),
    RateNetworkDD: selectFromRateAdminRatesReducer(state, "RateNetworkDD"),
    locationDD: selectFromLocationReducer(state, "locationDD"),
    isReservationsReservationsExportError:
      selectFromReservationsReservationsReducer(
        state,
        "isReservationsReservationsExportError",
      ),
    isReservationsReservationsExportSuccess:
      selectFromReservationsReservationsReducer(
        state,
        "isReservationsReservationsExportSuccess",
      ),
    reservationFilters: selectFromReservationsReservationsReducer(
      state,
      "reservationFilters",
    ),
    reservationSummary: selectFromReservationsReservationsReducer(
      state,
      "reservationSummary",
    ),
    tableColumnFilters: selectFromReservationsReservationsReducer(
      state,
      "tableColumnFilters",
    ),
    savedTableState: selectFromReservationsReservationsReducer(
      state,
      "savedTableState",
    ),
    ...ownProps,
  };
};

const selectFromReservationsReservationsReducer = (state, path) => {
  return selectReservationsReservationsReducer(state)[path];
};

const selectReservationsReservationsReducer = ({
  ReservationsReservationsReducer,
}) => {
  return ReservationsReservationsReducer;
};

// For Network
const selectFromRateAdminRatesReducer = (state, path) => {
  return selectRateAdminRatesReducer(state)[path];
};

const selectRateAdminRatesReducer = ({ rateadminRatesReducer }) => {
  return rateadminRatesReducer;
};
//Location
const selectFromLocationReducer = (state, path) => {
  return selectLocationReducer(state)[path];
};
const selectLocationReducer = ({ locationReducer }) => {
  return locationReducer;
};
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ReservationsReservationsScreen);
